"use client";

import { type KeyboardEvent, type ReactNode, useId, useRef } from "react";
import { InfoDot } from "../data-display/InfoDot";
import type { SrcCardSource } from "../data-display/SrcCard";
import { SrcCard } from "../data-display/SrcCard";
import { CiteHistory } from "../intake";
import type { HistoryStage } from "../intake/CiteHistory";
import { Clock, Pen, X } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";
import { PROV_META, TYPE_INFO } from "../lib/provenance";

export interface AssumptionDetailHistory {
  at: string;
  actorLabel: string;
  oldValue: string;
  newValue: string;
  oldSourceLabel?: string;
  newSourceLabel?: string;
  reason?: string;
}

export interface AssumptionDetailHistorySeed {
  formattedValue: string;
  provenance: ProvenanceType;
  origin: "compass" | "organization" | "opportunity";
  sources?: SrcCardSource[];
}

export interface AssumptionDetailRow {
  id: string;
  label: string;
  formattedValue: string;
  inputPrefix?: string;
  inputSuffix?: string;
  editable: boolean;
  organizationManaged: boolean;
  organizationBindable?: boolean;
  organizationEditable?: boolean;
  hasLocalOverride?: boolean;
  provenance: ProvenanceType;
  derivationLabel?: string;
  description?: string;
  snippet?: string;
  sources: SrcCardSource[];
  history: AssumptionDetailHistory[];
  historySeed: AssumptionDetailHistorySeed | null;
}

export interface AssumptionDetailRailProps {
  row: AssumptionDetailRow;
  tab: "source" | "history";
  editing: boolean;
  valueDraft: string;
  reasonDraft: string;
  useLocally?: boolean;
  /** True when saving with the local-only switch off would remove an existing
   *  local override and return the row to the organization value (the drafted
   *  value matches it) — a local draft change, so Save stays enabled even
   *  without organization write permission. */
  revertingToOrganization?: boolean;
  validationError?: string;
  onTabChange(tab: "source" | "history"): void;
  onStartEdit(): void;
  onValueDraftChange(value: string): void;
  onReasonDraftChange(reason: string): void;
  onUseLocallyChange?(value: boolean): void;
  onSave(): void;
  onCancel(): void;
  onClose(): void;
  onUseSourceValue?(source: SrcCardSource): void;
}

const PILL_BG: Record<string, string> = {
  prov: "bg-(--prov)",
  est: "bg-(--est)",
  cat: "bg-(--accent-plum)",
  empty: "bg-(--decide)",
};

function normalizedProvenance(
  provenance: ProvenanceType,
): Exclude<ProvenanceType, "vault"> {
  return provenance === "vault" ? "catalog" : provenance;
}

function initialHistoryStage(
  seed: AssumptionDetailHistorySeed,
  prior: boolean,
): HistoryStage {
  const provenance = normalizedProvenance(seed.provenance);
  let head: ReactNode;

  if (seed.origin === "compass") {
    head = (
      <>
        Generated at <b>{seed.formattedValue}</b> by Compass
      </>
    );
  } else {
    head = (
      <>
        Set to <b>{seed.formattedValue}</b>
        {seed.origin === "organization"
          ? " from the organization profile"
          : " for this opportunity"}
      </>
    );
  }

  return {
    type: provenance,
    head,
    meta: "This opportunity",
    refs: seed.sources ?? [],
    prior,
  };
}

function historyStages(row: AssumptionDetailRow): HistoryStage[] {
  const stages: HistoryStage[] = row.history.map((entry, index) => ({
    type: normalizedProvenance(row.provenance),
    head: (
      <>
        Changed from <span className="line-through">{entry.oldValue}</span> to{" "}
        <b>{entry.newValue}</b> by {entry.actorLabel}
        {entry.oldSourceLabel &&
        entry.newSourceLabel &&
        entry.oldSourceLabel !== entry.newSourceLabel ? (
          <span>
            {" "}
            ({entry.oldSourceLabel} → {entry.newSourceLabel})
          </span>
        ) : null}
      </>
    ),
    meta: entry.at,
    reason: entry.reason,
    prior: index > 0,
  }));

  if (row.historySeed) {
    stages.push(initialHistoryStage(row.historySeed, stages.length > 0));
  }

  if (stages.length === 0) {
    stages.push({
      type: "empty",
      head: <>No value has been set yet.</>,
      meta: "This opportunity",
    });
  }

  return stages;
}

function sourceKeys(sources: SrcCardSource[]): string[] {
  const occurrences = new Map<string, number>();
  return sources.map((source) => {
    const identity = JSON.stringify([
      source.kind,
      source.name,
      source.url ?? null,
      source.year ?? null,
      source.sub ?? null,
      source.by ?? null,
      source.blurb ?? null,
      source.body ?? null,
      source.used ?? null,
      source.value ?? null,
      source.valueLabel ?? null,
    ]);
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return occurrence === 0 ? identity : `${identity}#${occurrence + 1}`;
  });
}

export function AssumptionDetailRail({
  row,
  tab,
  editing,
  valueDraft,
  reasonDraft,
  useLocally,
  revertingToOrganization,
  validationError,
  onTabChange,
  onStartEdit,
  onValueDraftChange,
  onReasonDraftChange,
  onUseLocallyChange,
  onSave,
  onCancel,
  onClose,
  onUseSourceValue,
}: AssumptionDetailRailProps) {
  const instanceId = useId();
  const sourceTabRef = useRef<HTMLButtonElement>(null);
  const historyTabRef = useRef<HTMLButtonElement>(null);
  const provenance = normalizedProvenance(row.provenance);
  const needsInput = row.provenance === "empty";
  const provenanceMeta = PROV_META[provenance] ?? PROV_META.estimated;
  const canEdit = row.editable;
  const showEditor = canEdit && editing;
  const idPrefix = `assumption-detail-${instanceId}`;
  const sourceTabId = `${idPrefix}-source-tab`;
  const historyTabId = `${idPrefix}-history-tab`;
  const panelId = `${idPrefix}-panel`;
  const valueInputId = `${idPrefix}-value`;
  const reasonInputId = `${idPrefix}-reason`;
  const validationId = `${idPrefix}-validation`;
  const stableSourceKeys = sourceKeys(row.sources);

  function onTabListKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    let nextTab: "source" | "history";
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowRight":
        nextTab = tab === "source" ? "history" : "source";
        break;
      case "Home":
        nextTab = "source";
        break;
      case "End":
        nextTab = "history";
        break;
      default:
        return;
    }
    event.preventDefault();
    onTabChange(nextTab);
    (nextTab === "source" ? sourceTabRef : historyTabRef).current?.focus();
  }

  return (
    <section
      aria-label={`Details for ${row.label}`}
      className="box-border flex h-full w-full min-w-0 flex-col bg-(--bg-white)"
    >
      <header className="flex min-h-[48px] shrink-0 items-center gap-3 border-b border-(--divider-section) px-[18px] py-[13px]">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[.07em] text-(--fg-muted)">
            <span>Sources</span>
            <span aria-hidden="true" className="text-(--fg-subtle)">
              /
            </span>
            <span className="truncate normal-case tracking-normal text-(--fg-primary)">
              {row.label}
            </span>
          </div>
        </div>
        <button
          type="button"
          aria-label={`Close details for ${row.label}`}
          className="inline-flex shrink-0 items-center justify-center rounded-(--radius-xs) border-0 bg-transparent p-1 text-(--fg-muted) hover:bg-(--bg-hover) hover:text-(--fg-primary)"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </header>

      <div
        key={`${row.id}:${showEditor ? "editing" : "resting"}`}
        className="assumption-detail-content-swap min-h-0 flex-1 overflow-y-auto p-[18px] pb-6"
      >
        {showEditor ? (
          <div className="rounded-(--radius-container) border border-(--border-default) bg-(--bg-canvas) p-[13px]">
            <label
              htmlFor={valueInputId}
              className="mb-1.5 block text-[12px] font-semibold text-(--fg-secondary)"
            >
              Value for {row.label}
            </label>
            <div className="flex items-center rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) focus-within:border-(--fg-brand) focus-within:shadow-[inset_0_0_0_1px_var(--fg-brand)]">
              {row.inputPrefix && (
                <span className="pl-3 text-[14px] text-(--fg-secondary)">
                  {row.inputPrefix}
                </span>
              )}
              <input
                id={valueInputId}
                data-assumption-detail-focus="value"
                type="text"
                value={valueDraft}
                aria-invalid={validationError ? true : undefined}
                aria-describedby={validationError ? validationId : undefined}
                className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[16px] font-bold text-(--fg-primary) outline-none"
                onChange={(event) => onValueDraftChange(event.target.value)}
              />
              {row.inputSuffix && (
                <span className="pr-3 text-[13px] text-(--fg-secondary)">
                  {row.inputSuffix}
                </span>
              )}
            </div>
            {validationError && (
              <p
                id={validationId}
                role="alert"
                className="mb-0 mt-1.5 text-[12px] text-(--status-error)"
              >
                {validationError}
              </p>
            )}

            <label
              htmlFor={reasonInputId}
              className="mb-1.5 mt-3 block text-[12px] font-semibold text-(--fg-secondary)"
            >
              Reason for change (optional)
            </label>
            <textarea
              id={reasonInputId}
              value={reasonDraft}
              maxLength={1000}
              rows={3}
              placeholder="Add context that will help others understand this change."
              className="box-border w-full resize-y rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) px-3 py-2.5 text-[13px] text-(--fg-primary) outline-none focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]"
              onChange={(event) => onReasonDraftChange(event.target.value)}
            />

            {row.organizationBindable && (
              <div className="mt-3 border-t border-(--divider-row) pt-3">
                <label className="flex cursor-pointer items-start gap-2.5">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={useLocally ?? false}
                    aria-label="Use locally only, without updating the organization profile"
                    className={[
                      "relative mt-0.5 h-5 w-9 shrink-0 rounded-full border-0 transition-colors",
                      useLocally ? "bg-(--brand-navy)" : "bg-(--border-strong)",
                    ].join(" ")}
                    onClick={() => onUseLocallyChange?.(!(useLocally ?? false))}
                  >
                    <span
                      className={[
                        "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
                        useLocally ? "translate-x-[18px]" : "translate-x-0.5",
                      ].join(" ")}
                    />
                  </button>
                  <span className="text-[12px] leading-[1.45] text-(--fg-secondary)">
                    Use locally only, without updating the organization profile
                  </span>
                </label>
                {!useLocally && revertingToOrganization ? (
                  <p className="mb-0 mt-2 text-[12px] leading-[1.45] text-(--fg-muted)">
                    Saving removes the local value and returns this opportunity
                    to the organization value.
                  </p>
                ) : (
                  <>
                    {!row.organizationEditable && (
                      <p className="mb-0 mt-2 text-[12px] text-(--status-error-text)">
                        {useLocally
                          ? "Organization write permission is unavailable. This change will be saved locally only."
                          : "Organization write permission is required. Use locally only to save this opportunity."}
                      </p>
                    )}
                    {row.organizationEditable && (
                      <p className="mb-0 mt-2 text-[12px] leading-[1.45] text-(--fg-muted)">
                        {useLocally
                          ? "This edit will be staged in Pending changes. Select Confirm & save to apply it to this opportunity."
                          : "Saving will ask for confirmation, then update the organization and recalculate this opportunity immediately."}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={
                  row.organizationBindable &&
                  !useLocally &&
                  row.organizationEditable === false &&
                  !revertingToOrganization
                }
                className="rounded-(--radius-field) border-0 bg-(--brand-navy) px-3.5 py-2 text-[13px] font-semibold text-(--fg-inverse) hover:bg-(--steel-110) disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onSave}
              >
                Save
              </button>
              <button
                type="button"
                className="rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) px-3.5 py-2 text-[13px] font-semibold text-(--fg-secondary) hover:bg-(--bg-hover)"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div
                  className={[
                    "overflow-wrap-anywhere text-[34px] font-bold leading-[1.1] tracking-[-0.02em]",
                    needsInput ? "text-(--decide)" : "text-(--fg-primary)",
                  ].join(" ")}
                >
                  {row.formattedValue}
                </div>
                <div className="mt-1 text-[15px] tracking-[-0.01em] text-(--fg-secondary)">
                  {row.label}
                </div>
                {row.description && (
                  <p className="mb-0 mt-1.5 text-[12px] leading-[1.45] text-(--fg-muted)">
                    {row.description}
                  </p>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Edit ${row.label}`}
                  title="Override this value"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-(--radius-field) border-0 bg-transparent text-(--fg-muted) hover:bg-(--bg-hover) hover:text-(--fg-primary)"
                  onClick={onStartEdit}
                >
                  <Pen size={16} />
                </button>
              )}
            </div>

            {row.organizationManaged && (
              <div className="mt-3 rounded-(--radius-field) border border-(--border-default) bg-(--bg-canvas) p-3">
                <div className="text-[12px] font-semibold text-(--fg-primary)">
                  Organization-managed
                </div>
                <p className="mb-0 mt-1 text-[12px] leading-[1.45] text-(--fg-secondary)">
                  This value comes from your organization profile. Edit it to
                  update the organization, or choose Use locally only for this
                  opportunity.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mt-3.5 flex flex-wrap items-center gap-2">
          <span
            className={[
              "inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] pl-[10px]",
              "text-[13px] font-semibold text-white",
              PILL_BG[provenanceMeta.cls] ?? "bg-(--est)",
            ].join(" ")}
          >
            <provenanceMeta.icon size={13} />
            {provenanceMeta.label}
            {TYPE_INFO[provenance] && (
              <InfoDot text={TYPE_INFO[provenance]} onDark />
            )}
          </span>
          {row.derivationLabel && (
            <span className="inline-flex items-center rounded-full border border-(--border-default) bg-(--bg-canvas) px-3 py-[5px] text-[12px] font-semibold text-(--fg-secondary)">
              {row.derivationLabel}
            </span>
          )}
        </div>

        <div
          role="tablist"
          aria-label={`${row.label} details`}
          className="mt-4 flex gap-[22px] border-b border-(--divider-section)"
          onKeyDown={onTabListKeyDown}
        >
          <button
            ref={sourceTabRef}
            data-assumption-detail-focus="resting"
            id={sourceTabId}
            type="button"
            role="tab"
            aria-selected={tab === "source"}
            aria-controls={panelId}
            tabIndex={tab === "source" ? 0 : -1}
            className={[
              "-mb-px flex cursor-pointer items-center gap-1.5 border-0 border-b-2 bg-transparent pb-[9px] pt-[9px]",
              "text-[12px] font-semibold uppercase tracking-[.07em]",
              tab === "source"
                ? "border-b-(--fg-brand) text-(--fg-primary)"
                : "border-b-transparent text-(--fg-muted)",
            ].join(" ")}
            onClick={() => onTabChange("source")}
          >
            Source
          </button>
          <button
            ref={historyTabRef}
            data-assumption-detail-focus="resting"
            id={historyTabId}
            type="button"
            role="tab"
            aria-label={`History${row.history.length > 0 ? ` ${row.history.length}` : ""}`}
            aria-selected={tab === "history"}
            aria-controls={panelId}
            tabIndex={tab === "history" ? 0 : -1}
            className={[
              "-mb-px flex cursor-pointer items-center gap-1.5 border-0 border-b-2 bg-transparent pb-[9px] pt-[9px]",
              "text-[12px] font-semibold uppercase tracking-[.07em]",
              tab === "history"
                ? "border-b-(--fg-brand) text-(--fg-primary)"
                : "border-b-transparent text-(--fg-muted)",
            ].join(" ")}
            onClick={() => onTabChange("history")}
          >
            <Clock size={14} />
            History
            {row.history.length > 0 && (
              <span className="font-medium text-(--fg-muted)">
                {row.history.length}
              </span>
            )}
          </button>
        </div>

        <div
          key={tab}
          id={panelId}
          role="tabpanel"
          className="assumption-detail-content-swap"
          aria-labelledby={tab === "source" ? sourceTabId : historyTabId}
        >
          {tab === "history" ? (
            <CiteHistory stages={historyStages(row)} />
          ) : row.snippet || row.sources.length > 0 ? (
            <>
              {row.snippet &&
                (needsInput ? (
                  <p className="mb-0 mt-[14px] text-[14px] leading-[1.5] text-(--fg-primary)">
                    {row.snippet}
                  </p>
                ) : (
                  <blockquote className="mb-0 mt-[14px] rounded-r-(--radius-sm) border-l-[3px] border-l-(--border-default) bg-(--bg-canvas) px-[14px] py-3 text-[13.5px] leading-[1.5] text-(--fg-primary)">
                    &ldquo;{row.snippet}&rdquo;
                  </blockquote>
                ))}
              {row.sources.length > 0 && (
                <div className="mt-[13px] flex flex-col gap-2">
                  {row.sources.map((source, index) => (
                    <SrcCard
                      key={stableSourceKeys[index]}
                      source={source}
                      letter={String.fromCharCode(97 + index)}
                      variant="detail"
                      defaultExpanded={
                        needsInput || Boolean(source.blurb || source.url)
                      }
                      onUseValue={onUseSourceValue}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="my-6 text-center text-[13px] text-(--fg-muted)">
              No source details available.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
